import { FunctionComponent } from 'react';
import {
  PatronLocationEntity,
  geocodeResultToPatronLocation,
} from 'components/patron-location/PatronLocation';
import { PatronLocationFinderCurrentPosition } from 'components/patron-location/PatronLocationFinderCurrentPosition';
import { PatronLocationFinderHistory } from 'components/patron-location/PatronLocationFinderHistory';
import {
  PatronLocationFinderSearch,
  PatronLocationFinderSearchSuggestion,
} from 'components/patron-location/PatronLocationFinderSearch';
import { getAddressFromPlaceId } from 'utils/locationUtils';
import { useActions } from '@tock/shared/utils/connect';
import { useIsVisglMapsEnabled } from 'featureFlags/useIsVisglMapsEnabled';

interface PatronLocationFinderProps {
  activeLocation: PatronLocationEntity;
  googleMapsToken?: google.maps.places.AutocompleteSessionToken;
  onLocation?: (location: PatronLocationEntity) => void;
  setLoadingText?: (loadingText?: string) => void;
}

export const PatronLocationFinder: FunctionComponent<PatronLocationFinderProps> = ({
  activeLocation,
  googleMapsToken,
  onLocation,
  setLoadingText,
}) => {
  const { actions } = useActions();
  // TODO(visgl-burn-in): remove the `useNewApi` flag and use only the modern
  // (vis.gl) `getAddressFromPlaceId` data source once
  // CONSUMER_ENABLE_GOOGLE_MAPS_API_UPDATE is fully rolled out.
  const useNewApi = useIsVisglMapsEnabled();
  const handleResult = (result: PatronLocationEntity) => {
    if (onLocation) {
      onLocation(result);
    }
  };

  const centerLatLng = new google.maps.LatLng(activeLocation.lat, activeLocation.lng);
  const handleSuggestion = async (suggestion: PatronLocationFinderSearchSuggestion) => {
    if (setLoadingText) {
      setLoadingText(suggestion.primaryText);
    }
    try {
      const result = await getAddressFromPlaceId(suggestion.placeId, useNewApi);
      handleResult(
        await geocodeResultToPatronLocation(result, actions.metroArea.findClosestMetroArea)
      );
      if (setLoadingText) {
        setLoadingText();
      }
    } catch (error) {
      const cause = error instanceof Error ? error : new Error(String(error));
      window.LOGGER?.error('Failed to resolve selected patron location', cause);
      actions.toast.showError("We couldn't look up that location. Please try again.");
      if (setLoadingText) {
        setLoadingText();
      }
    }
  };

  return (
    <>
      <PatronLocationFinderSearch
        centerLatLng={centerLatLng}
        handleSuggestion={handleSuggestion}
        initialValue={activeLocation?.textFull}
        sessionToken={googleMapsToken}
      />
      <PatronLocationFinderCurrentPosition onResult={handleResult} />
      <PatronLocationFinderHistory activeLocation={activeLocation} onResult={handleResult} />
    </>
  );
};
